import { Action, createReducer, on } from '@ngrx/store';

import { UserProfileActions } from './user-profile.actions';
import { UserProfileState } from './user-profile.state';
import { normalizeThemeMode } from '../../features/shared/models/common/themes';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { AppActions } from '../app/app.actions';

export const initialState: UserProfileState = {
  loading: false,
  columns: {},
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      UserProfileActions.LoadProfile,
      (state): UserProfileState => ({
        ...state,
        loading: true,
        error: undefined,
      }),
    ),
    on(
      AppActions.LoadInitDataSuccess,
      (state, action): UserProfileState => ({
        ...state,
        columns: action.data?.assetSettings?.columns || {},
        datapointCompareSettings: action.data?.userSettings?.datapointCompareSettings,
        unlockedEntities: action.data?.userSettings?.unlockedEntities,
        completedTourIds: action.data?.userSettings?.toursCompleted,
        positionTableHiddenColumns: action.data?.userSettings?.positionTableHiddenColumns,
        positionFilters: action.data?.userSettings?.positionFilters,
        transactionTaskSettings: action.data?.userSettings?.transactionTaskSettings,
        pinnedTools: action.data?.userSettings?.pinnedTools,
        pinnedToolsByWorkspace: action.data?.userSettings?.pinnedToolsByWorkspace,
        favoriteDatapointFieldIds: action.data?.userSettings?.favoriteDatapointFieldIds || [],
        dealDockLayouts: action.data?.userSettings?.dealDockLayouts || [],
        dealDockLayoutsByWorkspace: action.data?.userSettings?.dealDockLayoutsByWorkspace,
      }),
    ),
    on(
      UserProfileActions.SetDatapointCompareSettingsSuccess,
      (state, action): UserProfileState => ({
        ...state,
        datapointCompareSettings: action.datapointCompareSettings,
      }),
    ),
    on(
      UserProfileActions.SetDealDockLayouts,
      (state, action): UserProfileState =>
        action.workspaceId
          ? {
              ...state,
              dealDockLayoutsByWorkspace: { ...state.dealDockLayoutsByWorkspace, [action.workspaceId]: action.dealDockLayouts },
            }
          : { ...state, dealDockLayouts: action.dealDockLayouts },
    ),
    on(
      UserProfileActions.UpdateProfile,
      (state): UserProfileState => ({
        ...state,
        loading: true,
        error: undefined,
      }),
    ),
    on(UserProfileActions.LoadProfileSuccess, UserProfileActions.UpdateProfileSuccess, (state, action): UserProfileState => {
      const theme = normalizeThemeMode(action.profile?.theme);
      // Only a full load fetches the tenants; a settings save keeps the ones already loaded.
      const tenantFields =
        action.type === UserProfileActions.UpdateProfileSuccess.type
          ? { activeTenantShortName: state.profile?.activeTenantShortName, tenants: state.profile?.tenants }
          : {};
      return {
        ...state,
        loading: false,
        error: undefined,
        profile: action.profile ? { ...action.profile, ...tenantFields, theme } : action.profile,
        theme,
      };
    }),

    on(
      UserProfileActions.UpdateAssetSetting,
      (state, action): UserProfileState => ({
        ...state,
        columns: {
          ...state.columns,
          ...(action.setting.columns || {}),
        },
      }),
    ),

    on(UserProfileActions.ResetPassword, (state): UserProfileState => ({ ...state, loading: true, error: undefined })),
    on(
      UserProfileActions.ResetPasswordSuccess,
      (state): UserProfileState => ({
        ...state,
        loading: false,
        error: undefined,
        passwordResetInitiated: true,
      }),
    ),
    on(UserProfileActions.SetColumns, (state, action): UserProfileState => ({ ...state, columns: action.columns })),
    on(
      UserProfileActions.UnlockEntitySuccess,
      (state, action): UserProfileState => ({ ...state, unlockedEntities: action.unlockedEntities }),
    ),
    on(
      UserProfileActions.ToggleCompletedToursSuccess,
      (state, action): UserProfileState => ({ ...state, completedTourIds: action.tourIds }),
    ),
    on(
      UserProfileActions.UpdatePositionTableHiddenColumnsSuccess,
      (state, action): UserProfileState => ({
        ...state,
        positionTableHiddenColumns: action.columns,
      }),
    ),
    on(
      UserProfileActions.UpdatePositionFiltersSuccess,
      (state, action): UserProfileState => ({
        ...state,
        positionFilters: action.filters,
      }),
    ),
    on(
      UserProfileActions.UpdateTransactionTaskSettingsSuccess,
      (state, action): UserProfileState => ({
        ...state,
        transactionTaskSettings: action.settings,
      }),
    ),
    on(
      UserProfileActions.UpdatePinnedToolsSuccess,
      (state, action): UserProfileState =>
        action.workspaceId
          ? {
              ...state,
              pinnedToolsByWorkspace: { ...state.pinnedToolsByWorkspace, [action.workspaceId]: action.pinnedTools },
            }
          : { ...state, pinnedTools: action.pinnedTools },
    ),
    on(UserProfileActions.ResetPinnedToolsSuccess, (state, action): UserProfileState => {
      if (!action.workspaceId) {
        return { ...state, pinnedTools: undefined };
      }
      // Drop only this workspace's entry — the flat pins are the fallback every other workspace
      // inherits, so clearing them here wiped pins the user never reset.
      const rest = Object.fromEntries(Object.entries(state.pinnedToolsByWorkspace ?? {}).filter(([id]) => id !== action.workspaceId));
      return { ...state, pinnedToolsByWorkspace: rest };
    }),
    on(
      UserProfileActions.SetFavoriteDatapointFieldIdsSuccess,
      (state, action): UserProfileState => ({
        ...state,
        favoriteDatapointFieldIds: action.favoriteDatapointFieldIds,
      }),
    ),
    // Keep the loaded profile and theme: a failed Settings refresh must not blank permissions or show the outage page.
    on(
      UserProfileActions.LoadProfileFail,
      UserProfileActions.UpdateProfileFail,
      UserProfileActions.ResetPasswordFail,
      UserProfileActions.SetDatapointCompareSettingsFail,
      (state, action): UserProfileState => ({
        ...state,
        loading: false,
        error: action.error,
      }),
    ),
  ),
);

export function reducer(state: UserProfileState | undefined, action: Action) {
  return featureReducer(state, action);
}
